import { Icon } from "@iconify/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Empty,
  Flex,
  Input,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { rewardService } from "../../../services/reward.service";
import useTitleStore from "../../../store/titleStore";
import pallete from "../../../utils/pallete";
import useDebounce from "../../../hooks/useDebounce";
import { customerService } from "../../../services/customer.service";
import CustomerInformation from "../../../components/RedeemReward/CustomerInformation";
import RenderIf from "../../../components/RenderIf";

const { Title, Text, Paragraph } = Typography;

export default function RedeemRewardIndex() {
  const { setTitle } = useTitleStore();
  const [messageApi, contextHolder] = message.useMessage();

  const [search, setSearch] = useState("");
  const [rewardSearch, setRewardSearch] = useState("");
  const [selectedRewardId, setSelectedRewardId] = useState(null);
  const [notes, setNotes] = useState("");
  const [otp, setOtp] = useState("");
  const [resendCountdown, setResendCountdown] = useState(45);

  const [customerData, setCustomerData] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const debouncedReward = useDebounce(rewardSearch, 500);

  const findCustomerMutation = useMutation({
    mutationFn: (nomen) => customerService.findByNomenForRedeem(nomen),
    onSuccess: ({ data }) => {
      setCustomerData(data)
    },
    onError: (err) => {
      setCustomerData(null);
      if (err.status === 404) {
        messageApi.error("Pelanggan tidak ditemukan");
        return
      }
      messageApi.error("Terjadi Kesalahan");
    },
    onMutate: () => {
      setCustomerData(null)
    }
  })

  const sendOtpMutation = useMutation({
    mutationFn: (nomen) => customerService.sendOtp(nomen),
    onSuccess: () => {
      messageApi.success("Kode OTP telah dikirim ke pelanggan.");
      setOtpSent(true);
      setResendCountdown(45);
    },
    onError: () => {
      messageApi.error("Gagal mengirim kode OTP.");
    },
  });

  const resendOtpMutation = useMutation({
    mutationFn: (nomen) => customerService.resend(nomen),
    onSuccess: () => {
      messageApi.success("Kode OTP telah dikirim ulang ke pelanggan.");
      setResendCountdown(45);
    },
    onError: () => {
      messageApi.error("Gagal mengirim ulang kode OTP.");
    },
  });

  const redeemRewardMutation = useMutation({
    mutationFn: () =>
      customerService.redeemReward({
        rewardId: String(selectedRewardId),
        otp,
        rewardNote: notes,
      }),
    onSuccess: () => {
      messageApi.success(
        "Verifikasi OTP berhasil. Reward diberikan kepada pelanggan."
      );
      setOtp("");
      setSelectedRewardId(null);
      setNotes("");
      findCustomerMutation.mutate(customerData?.nomen ?? search);
    },
    onError: () => {
      messageApi.error("Kode OTP tidak valid atau terjadi kesalahan.");
    },
  });

  const cancelRedeemMutation = useMutation({
    mutationFn: (nomen) => customerService.cancelRedeem(nomen),
    onSuccess: () => {
      messageApi.success("Klaim reward berhasil dihapus.");
      setCancelModalOpen(false);
      findCustomerMutation.mutate(customerData?.nomen ?? search);
    },
    onError: () => {
      messageApi.error("Gagal menghapus klaim reward.");
    },
  });

  const { data: rewardData, isLoading: isRewardLoading } = useQuery({
    queryKey: ["rewards-all", debouncedReward],
    queryFn: () => rewardService.fetchAllWithoutPaginate({ name: debouncedReward }),
    select: ({ data }) => data
  });

  useEffect(() => {
    setTitle("Redeem Reward");
  }, []);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setInterval(() => {
      setResendCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  const handleSearch = () => {
    if (search.trim() === "") {
      messageApi.error("Masukkan nomor nomen / sambungan");
      return
    };
    findCustomerMutation.mutate(search);
  };

  const handleReset = () => {
    setSearch("");
    setCustomerData(null);
    setSelectedRewardId(null);
    setOtp("");
    setNotes("");
    setOtpSent(false);
  };

  const handleSendOtp = () => {
    if (!selectedRewardId) {
      messageApi.error("Pilih reward terlebih dahulu.");
      return;
    }
    const nomen = customerData?.nomen ?? search;
    if (!nomen) {
      messageApi.error("Data pelanggan tidak ditemukan.");
      return;
    }
    setOtp("");
    sendOtpMutation.mutate(nomen);
  };

  const handleResendOtp = () => {
    if (!customerData?.nomen && !search) {
      messageApi.error("Data pelanggan tidak ditemukan.");
      return;
    }
    resendOtpMutation.mutate(customerData?.nomen ?? search);
  };

  const handleVerifyOtp = () => {
    if (otp.length < 6) {
      messageApi.error("Kode OTP harus 6 digit.");
      return;
    }
    if (!selectedRewardId) {
      messageApi.error("Pilih reward terlebih dahulu.");
      return;
    }
    redeemRewardMutation.mutate();
  };

  const handleCancel = () => {
    setOtp("");
    setSelectedRewardId(null);
    setNotes("");
    setOtpSent(false);
    messageApi.info("Transaksi redeem reward dibatalkan.");
  };

  const rewardColumns = [
    {
      title: "Nama Reward",
      dataIndex: "name",
      key: "name",
      render: (val) => <Text>{val}</Text>,
    },
    {
      title: "Deskripsi",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      // render: (text) => (
      //   <Text type="secondary" style={{ fontSize: 12 }}>
      //     {text || "-"}
      //   </Text>
      // ),
    },
    {
      title: "Status Ketersediaan",
      dataIndex: "available",
      key: "available",
      width: 200,
      render: (available) => {
        const isAvailable = Boolean(available);
        return (
          <Space size={6}>
            <Badge
              status={isAvailable ? "success" : "error"}
            />
            <Text type={isAvailable ? "success" : "danger"}>
              {isAvailable ? "Tersedia" : "Tidak Tersedia"}
            </Text>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "8px 4px" }}>
      {contextHolder}
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Flex align="center" justify="space-between" wrap="wrap" gap={12}>
          <div>
            <Tag color="blue" style={{ borderRadius: 999 }}>
              <Icon icon="mdi:star-circle-outline" style={{ marginRight: 4 }} />
              Loyalty & Retention Program
            </Tag>
            <Title level={3} style={{ marginTop: 12, marginBottom: 4 }}>
              Redeem Reward Pelanggan
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Berikan apresiasi & penukaran poin reward bagi pelanggan setia PAM
              JAYA dengan verifikasi aman melalui OTP.
            </Paragraph>
          </div>
        </Flex>
      </Card>

      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Typography.Paragraph style={{ margin: 0, fontSize: 15, fontWeight: 600, marginBottom: 13 }} >
          Pencarian Nomen / Nomor Sambungan Pelanggan
        </Typography.Paragraph>
        <Flex gap={12} wrap="wrap" align="center">
          <Input
            prefix={<Icon icon="mdi:magnify" />}
            placeholder="Masukkan nomor nomen / sambungan"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={handleSearch}
            style={{ flex: 1, minWidth: 280, backgroundColor: pallete.grey[100] }}
            size="large"
          />
          <Button
            type="primary"
            size="large"
            icon={<Icon icon="mdi:magnify" width={20} />}
            onClick={handleSearch}
            style={{ fontSize: 14 }}
            loading={findCustomerMutation.isPending}
          >
            Cari Data Pelanggan
          </Button>
          <Button size="large" style={{ fontSize: 14 }} onClick={handleReset}>
            Reset
          </Button>
        </Flex>
      </Card>

      <RenderIf when={!customerData} >
        <Card loading={findCustomerMutation.isPending} >
          <Empty
            description="Belum ada data pelanggan."
          />
        </Card>
      </RenderIf>

      <RenderIf when={customerData} >
        <CustomerInformation
          customer={customerData}
        />

        <RenderIf when={customerData?.rewardClaimed} >
          <Card>

            <Alert
              type="success"
              message={
                <Text type="success" style={{ fontWeight: 500, color: pallete.success.darkL }} >
                  Reward sudah diclaim
                </Text>
              }
              showIcon
              style={{ marginBottom: 18 }}
            />
            <Descriptions
              bordered
              size="small"
              column={2}
              styles={{
                label: {
                  backgroundColor: 'transparent',
                  borderInlineEnd: 'none',
                  fontWeight: 600,
                  color: pallete.grey[600],
                  fontSize: 12.5,
                  width: 200
                },
                content: {
                  fontWeight: 400,
                  fontSize: 12.5
                }
              }}
              items={[
                {
                  label: 'Jenis Reward',
                  children: customerData?.reward?.name
                },
                {
                  label: 'Petugas',
                  children: customerData?.rewardedBy
                },
                {
                  label: 'Deskripsi Reward',
                  children: customerData?.reward?.description
                },
                {
                  label: 'Waktu Klaim',
                  children: customerData?.rewardClaimedAt
                },
                {
                  label: 'Catatan Petugas',
                  children: customerData?.rewardNote ?? '-'
                },
              ]}
            />
            <Flex justify="end" style={{ marginTop: 20 }} >
              <Button
                variant="solid"
                color='danger'
                icon={<Icon icon="solar:trash-bin-trash-bold-duotone" />}
                onClick={() => setCancelModalOpen(true)}
              >
                Hapus Klaim
              </Button>
            </Flex>
          </Card>
        </RenderIf>

        <RenderIf when={!customerData?.rewardClaimed && customerData?.verificationStatus == 'accepted'} >
          <Card
            bordered={false}
            style={{ marginBottom: 16 }}
            title="Pilih Jenis Reward untuk Pelanggan"
            extra={
              <Flex gap={12} wrap="wrap" align="center" style={{}}>
                <Input
                  placeholder="Cari kode / nama reward..."
                  prefix={<Icon icon="mdi:magnify" />}
                  value={rewardSearch}
                  onChange={(e) => setRewardSearch(e.target.value)}
                  style={{ width: 260 }}
                  allowClear
                />
                <Tag color="blue">
                  Reward Terpilih: {selectedRewardId ? "1 Item" : "0 Item"}
                </Tag>
              </Flex>
            }
          >


            <Table
              columns={rewardColumns}
              dataSource={rewardData}
              size="small"
              loading={isRewardLoading}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: [selectedRewardId],
                onChange: (selectedRowKeys, selectedRows) => {
                  console.log({ selectedRowKeys, selectedRows, selectedRewardId })
                  if (selectedRowKeys.length === 0) {
                    setSelectedRewardId(null);
                    setOtpSent(false);
                    setOtp("");
                  } else {
                    if (selectedRowKeys[0] == selectedRewardId) {
                      setSelectedRewardId(null)
                      setOtpSent(false);
                      setOtp("");
                    } else {
                      setSelectedRewardId(selectedRowKeys[0]);
                      setOtpSent(false);
                      setOtp("");
                    }
                  }
                },
                getCheckboxProps: (record) => ({
                  disabled: !record.available,
                }),
              }}
              pagination={false}
              rowKey="id"
              locale={{ emptyText: "Tidak ada reward tersedia" }}
            />

            <Divider style={{ margin: "16px 0 12px" }} />
            <div style={{ marginBottom: 6 }}>
              <Text strong>Catatan Petugas Layanan / Keterangan Tambahan (Opsional):</Text>
            </div>
            <Input.TextArea
              rows={2}
              placeholder="Contoh: Permintaan langsung dari loket customer care pusat / konfirmasi via call center..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Card>

          <RenderIf when={selectedRewardId} >
            <Card
              bordered={false}
              style={{
                marginBottom: 16,
              }}
            >
              <Flex gap={12} align="center" style={{ marginBottom: 16 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: pallete.primary.main,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon icon="mdi:lock-outline" style={{ fontSize: 22 }} />
                </div>
                <div>
                  <Title level={5} style={{ marginBottom: 2 }}>
                    Otorisasi & Verifikasi OTP Pelanggan
                  </Title>
                  <Text type="secondary">
                    Kirim 6-digit kode verifikasi ke smartphone pelanggan untuk mengonfirmasi pemotongan poin.
                  </Text>
                </div>
              </Flex>

              <Divider />

              <Row gutter={16}>
                <Col
                  xs={24}
                  md={11}
                >
                  <Card
                    size="small"
                    style={{ height: '100%' }}
                    styles={{ body: { height: '100%' } }}
                  >
                    <Flex vertical style={{ height: '100%', }} >
                      <Descriptions
                        column={1}
                        styles={{
                          content: {
                            textAlign: 'right'
                          }
                        }}
                        items={[
                          {
                            label: 'Tujuan Pengiriman',
                            children: (
                              <Typography.Text style={{ display: 'inline-block', width: '100%' }} >
                                {customerData?.number}
                              </Typography.Text>
                            )
                          },
                          {
                            label: 'Metode Pengiriman',
                            children: (
                              <Typography.Text style={{ display: 'inline-block', width: '100%', color: pallete.primary.main }} >
                                WhatsApp Official PAM
                              </Typography.Text>
                            )
                          },
                        ]}
                      />
                      <Divider style={{ marginTop: 'auto', marginBottom: 12 }} />
                      {/* <div style={{ flex: 1 }} ></div> */}
                      <RenderIf when={!otpSent}>
                        <Button
                          block
                          icon={<Icon icon="mdi:send-outline" />}
                          onClick={handleSendOtp}
                          loading={sendOtpMutation.isPending}
                          variant="solid"
                          size="large"
                          style={{
                            fontSize: 14,
                          }}
                          color="primary"
                          type="primary"
                        >
                          Kirim OTP
                        </Button>
                      </RenderIf>

                      <RenderIf when={otpSent}>
                        <div style={{ textAlign: "center" }}>
                          <Text type="secondary">
                            Dapat dikirim ulang dalam:{" "}
                            <Text strong style={{ color: "#fa8c16" }}>
                              01:{String(resendCountdown).padStart(2, "0")}
                            </Text>{" "}
                            detik
                          </Text>
                        </div>
                        <Button
                          block
                          icon={<Icon icon="mdi:refresh" />}
                          onClick={handleResendOtp}
                          loading={resendOtpMutation.isPending}
                          variant="filled"
                          size="large"
                          style={{
                            border: '1px solid ' + pallete.primary.main,
                            marginTop: 10,
                            fontSize: 14
                          }}
                          color="primary"
                        >
                          Kirim Ulang Kode OTP
                        </Button>
                      </RenderIf>

                    </Flex>
                  </Card>
                </Col>
                <Col xs={24} md={13}>
                  <Card size="small" bordered={false} style={{ height: "100", boxShadow: 'none' }}>
                    <div style={{ marginBottom: 10 }}>
                      <Text strong>Masukkan 6-Digit Kode OTP dari Pelanggan:</Text>
                    </div>
                    <Flex gap={8} wrap="wrap" style={{ marginBottom: 8 }}>
                      <Input.OTP
                        size="large"
                        value={otp}
                        onInput={(val) => setOtp(val.join(''))}
                      />
                    </Flex>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Minta pelanggan untuk menyebutkan 6 digit kode OTP yang diterima melalui ponsel pelanggan.
                    </Text>
                    <Divider style={{ margin: "12px 0" }} />
                    <Flex gap={8} wrap="wrap">
                      <Button
                        type="primary"
                        size="large"
                        icon={<Icon icon="mdi:check-circle-outline" />}
                        onClick={handleVerifyOtp}
                        loading={redeemRewardMutation.isPending}
                        style={{ flex: 1, minWidth: 220, fontSize: 14 }}
                      >
                        Verifikasi OTP & Berikan Reward
                      </Button>
                      <Button size="large" style={{ fontSize: 14 }} onClick={handleCancel}>
                        Batalkan Transaksi
                      </Button>
                    </Flex>
                  </Card>
                </Col>
              </Row>

              <Alert
                style={{ marginTop: 16 }}
                type="warning"
                showIcon
                icon={<Icon icon="mdi:alert-circle-outline" />}
                message={
                  <span>
                    <Text strong style={{ color: "#874d00" }}>
                      Perhatian Petugas:
                    </Text>
                    Pastikan identitas pelanggan sesuai dengan KTP & data Nomen sebelum
                    menginput kode OTP demi mencegah penyalahgunaan Reward PAM JAYA
                    JAYA.
                  </span>
                }
              />
            </Card>
          </RenderIf>
        </RenderIf>

      </RenderIf>

      <Modal
        title={
          <Flex align="center" gap={8}>
            <Icon icon="mdi:alert-circle-outline" style={{ color: "#fa8c16", fontSize: 22 }} />
            <span>Konfirmasi Hapus Klaim</span>
          </Flex>
        }
        open={cancelModalOpen}
        onCancel={() => setCancelModalOpen(false)}
        okText="Hapus Klaim"
        cancelText="Batal"
        okButtonProps={{
          danger: true,
          loading: cancelRedeemMutation.isPending,
          disabled: cancelRedeemMutation.isPending,
        }}
        cancelButtonProps={{
          disabled: cancelRedeemMutation.isPending,
        }}
        onOk={() =>
          cancelRedeemMutation.mutate(customerData?.nomen ?? search)
        }
      >
        <Typography.Paragraph style={{ marginBottom: 0 }}>
          Apakah Anda yakin ingin menghapus klaim reward untuk pelanggan ini?
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
          Tindakan ini akan membatalkan klaim reward{" "}
          <Text strong>{customerData?.reward?.name ?? "-"}</Text>
        </Typography.Paragraph>
      </Modal>
    </div>
  );
}